// The one transport, and the policy every request carries.
//
// The generated client under ./gen knows the routes and the shapes and nothing
// else. What it does not know is decided here, once: where the API is mounted,
// how the caller proves who they are, which organisation they are acting in,
// and what a 401 means.
//
// The source's console had a hand-written api.ts and a second sdk.ts. There is
// one generated client now and one credential; this file is where that
// credential is attached.
//
// # Two ways to prove who you are
//
// In production the bearer is the access token the app's own accounts minted
// (lib/auth.ts). In development and in the end-to-end suite the server runs
// its header verifier, and the caller names their subject in X-Test-Subject
// instead — the same seam the Go suite uses. Which one is in force is a
// build-time setting (VITE_AUTH_MODE), never a runtime one, so a production
// bundle cannot be talked into the test seam.

import { AUTH_MODE, freshAccessToken, refresh } from '../lib/auth';
import { client } from './gen/client.gen';

/** Where the API is mounted. The dev server proxies this to the Go binary. */
export const BASE = '/api';

/**
 * Thrown for any non-2xx response. Carries the status and the parsed error
 * body, because the server's refusals are data rather than prose — a rejected
 * filter names the property that was wrong — and a form that discards that
 * throws away the half of the error that is actionable.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem,
  ) {
    super(problem.message || problem.detail || problem.title || `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

/** The error shape the server answers with: Huma's model, plus a code. */
export interface Problem {
  code?: string;
  message?: string;
  title?: string;
  status?: number;
  detail?: string;
  details?: { message: string; location?: string; value?: unknown }[];
  errors?: { message: string; location?: string; value?: unknown }[];
}

/**
 * Points the generated client at the API and gives it the caller's credential.
 * Call once, before anything uses the SDK. The credential is read per request
 * rather than captured, because it changes mid-session — a renewal replaces
 * it — and a value captured here would be the one current at start-up.
 *
 * A 401 is one chance to renew: the refresh token is traded for a fresh pair
 * and the caller told to re-fetch. A second refusal, or nothing to renew
 * with, signs the browser out.
 *
 * X-Org-ID is deliberately NOT set here. The document says it is required, the
 * generated types say so too, and every call site passes it — see useOrg. An
 * interceptor that filled it in silently would make the type a lie and leave
 * "which organisation is this request for" invisible at the place it is
 * decided.
 */
let configured = false;
let handlers = { renewed: () => {}, unauthenticated: () => {} };

export function configureClient(onRenewed: () => void, onUnauthenticated: () => void): void {
  // Interceptors accumulate, and React runs an effect twice in development,
  // so they are registered once and only the handlers are replaced.
  handlers = { renewed: onRenewed, unauthenticated: onUnauthenticated };
  if (configured) return;
  configured = true;
  client.setConfig({ baseUrl: BASE });
  client.interceptors.request.use(async (request) => {
    const credential = await freshAccessToken();
    if (credential) {
      if (AUTH_MODE === 'header') request.headers.set('X-Test-Subject', credential);
      else request.headers.set('Authorization', `Bearer ${credential}`);
    }
    return request;
  });
  client.interceptors.response.use(async (response) => {
    if (response.status === 401) {
      if (await refresh()) handlers.renewed();
      else handlers.unauthenticated();
    }
    return response;
  });
}

/**
 * Unwraps a generated call into its value, or throws {@link ApiError}.
 *
 * The SDK answers `{ data, error }` rather than throwing, which is the wrong
 * default for TanStack Query: a query that resolves with an error object is a
 * query that reports success. This is the adapter, in one place.
 */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response?: Response }>,
): Promise<T> {
  const { data, error, response } = await call;
  if (!response) {
    throw new ApiError(0, { title: 'Network error', detail: String(error ?? 'request failed') });
  }
  if (!response.ok) {
    throw new ApiError(response.status, (error as Problem) ?? { status: response.status });
  }
  return data as T;
}
